import React, { useState, useEffect } from "react";
import InfoPage from ".";

const ResetPassword = () => {
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("/host-login");

  useEffect(() => {
    // Extract token from URL
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setSubmitStatus('invalid');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords don't match");
      setIsSubmitting(false);
      return;
    }
    
    if (newPassword.length < 8) {
      setErrorMessage("Password must be at least 8 characters");
      setIsSubmitting(false);
      return;
    }
    
    try {
      const res = await fetch('/api/user/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });
      
      const json = await res.json();
      
      if (res.ok && json.ok) {
        setSubmitStatus('success');
        // Postaway Academy "claim access" links carry ?next=/learn so the
        // person lands back in the hub after signing in.
        const params = new URLSearchParams(window.location.search);
        const next = params.get('next') || '';
        const target = /^\/learn(\/|$)/.test(next)
          ? `/login?next=${encodeURIComponent(next)}`
          : (json.redirectUrl || '/host-login');
        setRedirectUrl(target);
        // Redirect to appropriate login after 3 seconds
        setTimeout(() => {
          window.location.href = target;
        }, 3000);
      } else {
        setSubmitStatus('error');
        setErrorMessage(json.message || 'Reset link is invalid or expired');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const belowContent = (
    <>
      <div className="login-left">
        <img src="/house.png" alt="Create Password" className="login-image" />
        <p className="login-welcome">Almost there</p>
      </div>

      <div className="login-right">
        <form onSubmit={handleSubmit} className="login-form">
          <h3 className="login-title">Create New Password</h3>
          
          {submitStatus === 'success' ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p style={{ color: '#28a745', marginBottom: '16px', fontSize: '18px' }}>
                ✓ Password reset successfully!
              </p>
              <p style={{ fontSize: '14px', color: '#6b7280' }}>
                Redirecting you to login...
              </p>
            </div>
          ) : submitStatus === 'invalid' ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p style={{ color: '#dc1d08', marginBottom: '16px' }}>
                Invalid reset link
              </p>
              <div className="login-signup-link-below">
                <a href="/forgot-password">Request a new one</a>
              </div>
            </div>
          ) : (
            <>
              <div className="login-input-group">
                <div className="login-password-wrapper">
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)} 
                    required 
                    minLength="8"
                    className="login-input login-input-password" 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(s => !s)} 
                    aria-label="Toggle password visibility"
                    className="login-password-toggle"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" /><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" /><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" /><path d="m2 2 20 20" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
                <label className="login-label">New Password</label>
              </div>

              <div className="login-input-group">
                <input 
                  type={showPassword ? 'text' : 'password'} 
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                  required 
                  className="login-input" 
                />
                <label className="login-label">Confirm Password</label>
                {confirmPassword && newPassword && confirmPassword === newPassword && (
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#28a745', 
                    marginTop: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="8" r="8" fill="#28a745"/>
                      <path d="M4.5 8L7 10.5L11.5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Passwords match
                  </div>
                )}
              </div>

              <button type="submit" disabled={isSubmitting} className="login-submit">
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </button>

              {errorMessage && (
                <p className="login-error">{errorMessage}</p>
              )}
            </>
          )}
        </form>
      </div>
    </>
  );

  return <InfoPage title="Reset Password" divider belowContent={belowContent} disableStamps />;
};

export default ResetPassword;

